import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line, Bar } from 'react-chartjs-2';
import { Panel, Segmented, ChartBox } from './Dashui';
import { useChartTheme, baseOptions, barStyle, filterDataByRange, monthLabel, RANGES } from './Chartkit';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const useLoad = (url, pick, enabled) => {
  const [state, setState] = useState({ data: null, loading: true, error: '' });
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: '' }));
    (async () => {
      try {
        const res = await axios.get(url, auth());
        if (!cancelled) setState({ data: pick(res.data), loading: false, error: '' });
      } catch (err) {
        console.error(`Error fetching ${url}:`, err);
        if (!cancelled) setState({ data: null, loading: false, error: err.response?.data?.message || 'We couldn’t load this chart.' });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, enabled, reload]);
  return { ...state, retry: () => setReload((n) => n + 1) };
};

const DashAnalytics = ({ userData }) => {
  const { c } = useChartTheme();
  const ready = Boolean(userData?.id);

  // Same endpoints as before (the main + paid charts share one call)
  const main = useLoad(`${API}/api/admin/dashboard/statisticsgraph`, (d) => d?.data || [], ready);
  const demo = useLoad(`${API}/api/admin/dashboard/demo-statistics`, (d) => d?.data || d || [], ready);

  const [mainRange, setMainRange] = useState('6months');
  const [paidRange, setPaidRange] = useState('6months');
  const [demoRange, setDemoRange] = useState('6months');

  /* ---------- Monthly overview (line) ---------- */
  const now = new Date();
  const cm = now.getMonth() + 1;
  const cy = now.getFullYear();
  const lm = cm === 1 ? 12 : cm - 1;
  const ly = cm === 1 ? cy - 1 : cy;
  const nm = cm === 12 ? 1 : cm + 1;
  const ny = cm === 12 ? cy + 1 : cy;

  const mainRows = filterDataByRange(main.data, mainRange);
  const notFuture = (r) => r.year < cy || (r.year === cy && r.month <= cm);
  const currentIndex = mainRows.findIndex((r) => r.year === cy && r.month === cm);

  const mainChart = {
    labels: mainRows.map((r) => monthLabel(r.year, r.month)),
    datasets: [
      {
        label: 'Completed sessions',
        data: mainRows.map((r) => (notFuture(r) ? r.nbsessionsdone : null)),
        borderColor: c.s1,
        backgroundColor: c.s1,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: false,
      },
      {
        label: 'Students',
        data: mainRows.map((r) => (notFuture(r) ? r.nbofstudents : null)),
        borderColor: c.s2,
        backgroundColor: c.s2,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: false,
      },
      {
        // same rule as before: last month + this month = upcoming + done; next month = upcoming
        label: 'Booked (done + upcoming)',
        data: mainRows.map((r) => {
          if ((r.year === ly && r.month === lm) || (r.year === cy && r.month === cm)) return (r.nbsessionsupcoming || 0) + (r.nbsessionsdone || 0);
          if (r.year === ny && r.month === nm) return r.nbsessionsupcoming || 0;
          return null;
        }),
        borderColor: c.s3,
        backgroundColor: c.s3,
        borderWidth: 2,
        borderDash: [5, 5],
        tension: 0.3,
        pointRadius: 5,
        pointHoverRadius: 7,
        spanGaps: false,
      },
    ],
  };

  const mainOptions = baseOptions(c, { integer: true, yTitle: 'Sessions / students' });
  mainOptions.plugins.tooltip.callbacks = {
    label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y ?? 'No data'}`,
  };
  if (currentIndex >= 0) {
    mainOptions.plugins.annotation = {
      annotations: {
        thisMonth: {
          type: 'line',
          scaleID: 'x',
          value: currentIndex,
          borderColor: c.muted,
          borderWidth: 1,
          borderDash: [4, 4],
          label: {
            display: true, // plugin v2+
            enabled: true, // plugin v1 (old option name)
            content: 'This month',
            position: 'start',
            backgroundColor: c.tip,
            color: '#FFFFFF',
            font: { size: 11, weight: '600' },
            padding: 4,
          },
        },
      },
    };
  }

  /* ---------- Paid analysis (bars) ---------- */
  const paidRows = filterDataByRange(main.data, paidRange);
  const paidChart = {
    labels: paidRows.map((r) => monthLabel(r.year, r.month)),
    datasets: [
      { label: 'Completed paid sessions', data: paidRows.map((r) => r.nbsessionsdone || 0), ...barStyle(c.s1) },
      { label: 'Paid students', data: paidRows.map((r) => r.nbofstudents || 0), ...barStyle(c.s2) },
    ],
  };
  const paidOptions = baseOptions(c, { integer: true });

  /* ---------- Demo analysis (bars) ---------- */
  const demoRows = filterDataByRange(Array.isArray(demo.data) ? demo.data : [], demoRange);
  const demoChart = {
    labels: demoRows.map((r) => monthLabel(r.year, r.month)),
    datasets: [
      { label: 'Completed demos', data: demoRows.map((r) => r.completedDemos || 0), ...barStyle(c.s1) },
      { label: 'Enrolled', data: demoRows.map((r) => r.enrolledStudents || 0), ...barStyle(c.s3) },
    ],
  };
  const demoOptions = baseOptions(c, { integer: true });
  demoOptions.plugins.tooltip.callbacks = {
    // same extra line as before: conversion for the month
    afterBody: (items) => {
      const i = items[0]?.dataIndex;
      const done = demoChart.datasets[0].data[i] || 0;
      const enrolled = demoChart.datasets[1].data[i] || 0;
      return done > 0 ? `Conversion: ${((enrolled / done) * 100).toFixed(1)}%` : '';
    },
  };

  const totals = (rows, key) => rows.reduce((t, r) => t + (r[key] || 0), 0);

  return (
    <>
      <Panel
        title="Monthly overview"
        sub="Completed sessions, students and what’s booked"
        actions={<Segmented label="Range" options={RANGES} value={mainRange} onChange={setMainRange} />}
      >
        <ChartBox loading={main.loading} error={main.error} onRetry={main.retry} empty={!mainRows.length} emptyText="No data for this range." tall>
          <Line data={mainChart} options={mainOptions} />
        </ChartBox>
      </Panel>

      <div className="rmadd-grid-2">
        <Panel
          title="Paid classes"
          sub={main.loading ? '' : `${totals(paidRows, 'nbsessionsdone')} completed sessions in this range`}
          actions={<Segmented label="Range" options={RANGES} value={paidRange} onChange={setPaidRange} />}
        >
          <ChartBox loading={main.loading} error={main.error} onRetry={main.retry} empty={!paidRows.length} emptyText="No paid classes in this range.">
            <Bar data={paidChart} options={paidOptions} />
          </ChartBox>
        </Panel>

        <Panel
          title="Trial classes"
          sub={
            demo.loading
              ? ''
              : (() => {
                  const done = totals(demoRows, 'completedDemos');
                  const enr = totals(demoRows, 'enrolledStudents');
                  return `${enr} enrolled from ${done} trials${done ? ` · ${Math.round((enr / done) * 100)}%` : ''}`;
                })()
          }
          actions={<Segmented label="Range" options={RANGES} value={demoRange} onChange={setDemoRange} />}
        >
          <ChartBox loading={demo.loading} error={demo.error} onRetry={demo.retry} empty={!demoRows.length} emptyText="No trial classes in this range.">
            <Bar data={demoChart} options={demoOptions} />
          </ChartBox>
        </Panel>
      </div>
    </>
  );
};

export default DashAnalytics;